import React, { useState, useEffect } from "react";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import TeamMembersRow from "../teamMembersRow/teamMembersRow";
import { useParams, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  Tech: "bg-blue-500",
  Art: "bg-green-500",
  Music: "bg-purple-500",
  Science: "bg-teal-500",
  Content: "bg-yellow-600",
  Fashion: "bg-pink-400",
  Gaming: "bg-red-400",
  Food: "bg-orange-500",
  Craft: "bg-indigo-400",
};

const DetailsSection = ({ project, openRoles, ownerProfileImage, ownerEmail }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Applicant-side form state
  const [agreeTime, setAgreeTime] = useState(false);
  const [agreeSkills, setAgreeSkills] = useState(false);
  const [answers, setAnswers] = useState(["", ""]); // mirrors selectedRole.questions positions
  const [linksInput, setLinksInput] = useState(""); // comma/space separated URLs
  const [extraNotes, setExtraNotes] = useState("");
  const [formError, setFormError] = useState("");

  const { projectId } = useParams();
  const navigate = useNavigate();
  const currentUserEmail = localStorage.getItem("userEmail");

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/projects/${projectId}/team-members-public`);
        const data = await res.json();
        if (res.ok) setTeamMembers(data.teamMembers);
      } catch (e) {
        console.error("❌ Team fetch error", e);
      }
    };
    fetchTeamMembers();
  }, [projectId]);

  const handleShare = () => setIsShareModalOpen(true);

  const handleOpenModal = async (role) => {
    try {
      // Fetch full role details from backend
      const res = await fetch(`${BACKEND_URL}/roles/${role.id}`);
      if (res.ok) {
        const fullRole = await res.json();
        // ✅ Unwrap if backend sends { role: {...} }
        setSelectedRole(fullRole.role || fullRole);
      } else {
        setSelectedRole(role); // fallback if fetch fails
      }
    } catch {
      setSelectedRole(role); // fallback
    }

    // Initialize form state
    setAgreeTime(false);
    setAgreeSkills(false);
    const qLen = Array.isArray(role?.questions) ? role.questions.length : 0;
    setAnswers(new Array(Math.max(qLen, 2)).fill("")); // ensure 2 slots minimum
    setLinksInput("");
    setExtraNotes("");
    setFormError("");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedRole(null);
    setIsModalOpen(false);
  };

  const handleOwnerClick = () => {
    if (!ownerEmail) return;
    const username = ownerEmail.split("@")[0];
    navigate(`/${username}`);
  };

  // Simple URL validator
  const isValidUrl = (u) => {
    if (!u) return false;
    try {
      // Allow bare domains starting with http(s)
      const url = new URL(u.trim());
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleApply = async (roleId) => {
    // Prevent owners / members from applying
    if (teamMembers.some((member) => member.email === currentUserEmail)) {
      alert("✅ You're already on this team.");
      return;
    }

    // ✅ Validate required checkboxes
    if (!agreeTime || !agreeSkills) {
      setFormError("Please confirm you meet the time and skill requirements.");
      return;
    }

    // Build links array from input (comma/space separated)
    const links = linksInput
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0);

    // Optional: basic URL validation (ignore invalids, or block if you prefer)
    const invalidLinks = links.filter((l) => !isValidUrl(l));
    if (invalidLinks.length > 0) {
      setFormError("One or more links are not valid URLs. Please include http(s)://");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) return alert("You must be logged in.");

      // Fetch profile (for names & email)
      const profileRes = await fetch(`${BACKEND_URL}/auth/profile`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const profileData = await profileRes.json();
      const { firstName, lastName, email: applicantEmail } = profileData;

      // Compose application payload
      const payload = {
        message: `I am interested in the ${selectedRole.roleTitle} position.`,
        firstName,
        lastName,
        // 🆕 Agreements must be checked
        agreements: {
          timeCommitment: agreeTime,
          skills: agreeSkills,
        },
        // 🆕 Answers aligned to admin-defined questions
        answers, // array of strings, same indices as selectedRole.questions
        // 🆕 Applicant-provided extras
        links, // array of URLs
        extraNotes: extraNotes?.trim() || "",
      };

      // Submit application
      await fetch(`${BACKEND_URL}/applications/apply/${roleId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      // Notifications (unchanged)
      await Promise.all([
        fetch(`${BACKEND_URL}/notifications`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            recipientEmail: ownerEmail,
            message: `${firstName} ${lastName} applied to "${project.title}" for "${selectedRole.roleTitle}"`,
            type: "application_submitted",
            actor: applicantEmail,
            projectId,
            projectTitle: project.title,
            roleTitle: selectedRole.roleTitle,
          }),
        }),
        fetch(`${BACKEND_URL}/notifications`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            recipientEmail: applicantEmail,
            message: `You applied to "${project.title}" for "${selectedRole.roleTitle}"`,
            type: "application_confirmation",
            actor: applicantEmail,
            projectId,
            projectTitle: project.title,
            roleTitle: selectedRole.roleTitle,
          }),
        }),
      ]);

      alert("✅ Application submitted!");
      window.location.reload();
    } catch (error) {
      console.error("❌ Apply error:", error);
      alert("Something went wrong.");
    }
  };

  const categoryClass = CATEGORY_COLORS[project.category] || "bg-gray-400";

  return (
    <div className="relative w-full max-w-3xl mx-auto p-6 bg-white rounded-2xl shadow-md flex flex-col gap-6">
      {/* Avatar + Share */}
      <div className="flex justify-between items-center">
        <div onClick={handleOwnerClick} className="cursor-pointer">
          <img
            src={ownerProfileImage || "/person.png"}
            alt="Owner"
            className="w-14 h-14 rounded-full object-cover border border-gray-300"
            onError={(e) => (e.target.src = "/person.png")}
          />
        </div>

        <div className="relative group">
          <button
            onClick={handleShare}
            className="p-2 hover:bg-gray-100 rounded-full transition"
            aria-label="Share"
          >
            <ArrowUpRightIcon className="w-5 h-5 text-gray-600" />
          </button>
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs bg-black text-white rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition">
            Share
          </span>
        </div>
      </div>

      {/* Title, Category, Team */}
      <div className="flex flex-col gap-3">
        <h2 className="text-2xl font-bold text-gray-900 break-words">{project.title}</h2>
        <div className="flex items-center flex-wrap gap-3">
          {project.category && (
            <span className={`text-white text-xs font-semibold px-3 py-1 rounded-md uppercase ${categoryClass}`}>
              {project.category}
            </span>
          )}
          <TeamMembersRow projectId={projectId} />
        </div>
      </div>

      {/* Tags */}
      {project.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, i) => (
            <span key={i} className="text-xs bg-gray-200 text-gray-800 px-3 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-gray-600">{project.oneLiner}</p>
      <p className="text-sm text-gray-700 leading-relaxed">{project.description}</p>

      {/* Open Roles */}
      {openRoles?.length > 0 && (
        <div className="bg-gray-100 p-4 rounded-lg border border-blue-100">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Open Positions</h4>
          <div className="flex flex-col gap-2">
            {openRoles.map((role) => (
              <div key={role.id} className="flex justify-between items-center gap-4">
                <p className="text-sm font-medium text-gray-900 break-words w-full max-w-xs">{role.roleTitle}</p>
                <button
                  onClick={() => handleOpenModal(role)}
                  className="text-white bg-black text-xs font-semibold px-3 py-1 rounded hover:bg-gray-900"
                >
                  Join
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {isModalOpen && selectedRole && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-3">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl animate-fadeIn scale-95 sm:scale-100">
            {/* Header */}
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">{selectedRole.roleTitle}</h2>
              <button
                onClick={handleCloseModal}
                className="text-2xl font-bold text-gray-400 hover:text-gray-600 transition"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-6">
              {/* Time Commitment */}
      <div className="flex items-center justify-between gap-4 w-full">
        {/* Card */}
        <div className="w-80 h-24 bg-white rounded-[20px] shadow-[0px_4px_10px_0px_rgba(163,163,163,0.25)]
                        flex flex-col justify-center px-5 py-3 
                        transition-all duration-300 transform hover:scale-105 hover:shadow-[0px_8px_20px_rgba(56,189,248,0.45)]
                        hover:ring-2 hover:ring-sky-300 hover:bg-gradient-to-r hover:from-sky-50 hover:to-cyan-50">
          {/* Top Row: label + tag */}
          <div className="flex justify-between items-center">
            <p className="text-sm font-medium text-gray-800">Time Commitment</p>
            {selectedRole?.timeCommitment ? (
              <div className="w-36 h-8 bg-cyan-100 rounded-md flex items-center justify-center shadow-sm hover:shadow-md transition">
                <span className="text-sky-500 text-sm font-semibold font-['Inter'] tracking-wide">
                  {selectedRole.timeCommitment} hrs/week
                </span>
              </div>
            ) : (
              <span className="text-sm text-gray-500">Not specified</span>
            )}
          </div>
        </div>

        {/* Checkbox on far right with label underneath */}
        <div className="flex flex-col items-center">
          <input
            type="checkbox"
            className="h-5 w-5 accent-sky-500 hover:scale-110 transition"
            checked={agreeTime}
            onChange={(e) => setAgreeTime(e.target.checked)}
          />
          <div className="text-neutral-400 text-xs font-medium font-['Inter'] mt-1">
            *check to agree
          </div>
        </div>
      </div>

      {/* Skill Requirements */}
      <div className="flex items-center justify-between gap-4 w-full">
        {/* Card */}
        <div
        className="min-w-[22rem] h-24 bg-white rounded-[20px] shadow-[0px_4px_10px_0px_rgba(163,163,163,0.25)]
                  flex flex-col justify-center px-5 py-3 
                  transition-all duration-300 transform 
                  hover:-rotate-1 hover:scale-105 
                  hover:shadow-[0px_8px_24px_rgba(59,130,246,0.35)]
                  hover:bg-gradient-to-r hover:from-blue-50 hover:to-white"
      >
          {/* Label */}
          <p className="text-sm font-medium text-gray-800 mb-2">Skill Requirements</p>

          {/* Skills Tag Boxes */}
          {selectedRole?.skills ? (
            <div className="flex gap-2 flex-wrap">
              {selectedRole.skills.split(",").map((s, i) => (
                <div
                  key={i}
                  className="h-8 px-3 bg-gradient-to-r from-cyan-100 to-sky-100 
                            rounded-md flex items-center shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <span className="text-sky-500 text-sm font-semibold font-['Inter'] whitespace-nowrap">
                    {s.trim()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-sm text-gray-500">Not specified</span>
          )}
        </div>

        {/* Checkbox on far right with label underneath */}
        <div className="flex flex-col items-center">
          <input
            type="checkbox"
            className="h-5 w-5 accent-sky-500 hover:scale-110 transition"
            checked={agreeSkills}
            onChange={(e) => setAgreeSkills(e.target.checked)}
          />
          <div className="text-neutral-400 text-xs font-medium font-['Inter'] mt-1">
            *check to agree
          </div>
        </div>
      </div>




              {/* Role Description */}
      <div className="transition-all duration-300 transform hover:scale-[1.01] hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 rounded-xl p-3">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Role Description</h3>
        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
          {selectedRole.description}
        </p>
      </div>

      {/* Questions */}
      {Array.isArray(selectedRole?.questions) && selectedRole.questions.length > 0 && (
        <div className="transition-all duration-300 hover:shadow-md hover:shadow-blue-100 rounded-xl p-3">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Questions</h3>
          {selectedRole.questions.map((q, idx) => (
            <div
              key={idx}
              className="mb-3 group transition-transform duration-200 hover:translate-x-1"
            >
              <label className="text-xs text-gray-600 mb-1 block group-hover:text-blue-600 transition">
                {q}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg text-sm 
                          focus:outline-none focus:ring-2 focus:ring-blue-500
                          transition-shadow duration-200 group-hover:shadow-sm"
                placeholder="Your answer..."
                value={answers[idx] || ""}
                onChange={(e) => {
                  const next = [...answers];
                  next[idx] = e.target.value;
                  setAnswers(next);
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Links */}
      <div className="transition-all duration-300 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 rounded-xl p-3">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Links</h3>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded-lg text-sm 
                    focus:outline-none focus:ring-2 focus:ring-cyan-400
                    transition-all duration-300 focus:shadow-md"
          placeholder="Resume/Portfolio URLs (comma or space separated)"
          value={linksInput}
          onChange={(e) => setLinksInput(e.target.value)}
        />
      </div>

      {/* Extra Notes */}
      <div className="transition-all duration-300 hover:shadow-lg hover:shadow-blue-100 rounded-xl p-3">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Extra Notes</h3>
        <textarea
          rows={3}
          className="w-full px-3 py-2 border rounded-lg text-sm resize-none 
                    focus:outline-none focus:ring-2 focus:ring-blue-400
                    transition-all duration-300 focus:shadow-md
                    hover:border-blue-300"
          placeholder="Anything else you'd like to share..."
          value={extraNotes}
          onChange={(e) => setExtraNotes(e.target.value)}
        />
      </div>



              {/* Error */}
              {formError && (
                <p className="text-red-600 text-sm">{formError}</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                onClick={handleCloseModal}
                className="bg-gray-100 text-gray-700 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApply(selectedRole.id)}
                className="bg-black text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-900 transition"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] px-3">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Share this project</h2>
              <button onClick={() => setIsShareModalOpen(false)} className="text-xl font-bold text-gray-500">×</button>
            </div>
            <input
              type="text"
              value={`https://frontera.app/projects/${projectId}/details`}
              readOnly
              className="w-full px-3 py-2 mb-4 border rounded text-sm"
            />
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`https://frontera.app/projects/${projectId}/details`);
                  alert("✅ Link copied!");
                } catch {
                  alert("❌ Failed to copy.");
                }
              }}
              className="bg-black text-white w-full py-2 rounded font-medium text-sm hover:bg-gray-900"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsSection;
